import axios from '@/api/axios-feedback'
import marked from 'marked'
import { downloadCsv, objectsToCsv } from '@/helpers/csv_exporter'
import plan from '@/constants/user/user-type.js'
import {
  formatDateToDDMMMYYYYHHmmString,
  formatDateToDDMMMYYYYString,
  formatDateToMMYYString,
  formatDateToMMYYYYString,
  formatDateToYYYYMMDDString,
} from '@/helpers/date_formatter'

import moment from 'moment'
import reasonCodes from '@/constants/ai-setting/reason-codes'

const state = {
  feedback: [],
  currentFeedbackPage: 1,
  totalFeedbackPage: 1,
  feedbackPageSize: 25,
  isLoadingFeedback: false,
  conversations: [],
  conversationName: null,
  hasMore: false,
  beforeId: 0,
}

const getters = {
  feedback: (state) => {
    return state.feedback
  },
  currentFeedbackPage: (state) => {
    return state.currentFeedbackPage
  },
  totalFeedbackPage: (state) => {
    return state.totalFeedbackPage
  },
  isLoadingFeedback: (state) => {
    return state.isLoadingFeedback
  },
  feedbackPageSize: (state) => {
    return state.feedbackPageSize
  },
  conversations: (state) => {
    return state.conversations
  },
  conversationName: (state) => {
    return state.conversationName
  },
  hasMore: (state) => {
    return state.hasMore
  },
  beforeId: (state) => {
    return state.beforeId
  },
}

const mutations = {
  setFeedback(state, requests) {
    state.feedback = requests
  },
  clearFeedback(state) {
    state.feedback = []
  },
  setFeedbackPage(state, page) {
    state.currentFeedbackPage = page
  },
  setTotalFeedbackPage(state, totalFeedbackPage) {
    state.totalFeedbackPage = totalFeedbackPage
  },
  setLoadingFeedback(state, status) {
    state.isLoadingFeedback = status
  },
  setConversations(state, conversations) {
    state.conversations = conversations
  },
  setConversationName(state, name) {
    state.conversationName = name
  },
  setIsFetchingMore(state, status) {
    state.isFetchingMore = status
  },
  setHasMore(state, status) {
    state.hasMore = status
  },
  setBeforeId(state, status) {
    state.beforeId = status
  },
}

const actions = {
  getFeedback: async ({ dispatch, commit, getters }, { type, ...params }) => {
    switch (type) {
      case 'general':
        dispatch('getGeneralFeedback')
        break
      case 'monthly-horoscope':
        params.feature = 'monthly_prophecy'
        dispatch('getMonthlyHoroscopeFeedback', params)
        break
      case 'profile':
        params.feature = 'profile_v2'
        dispatch('getProfileFeedback', params)
        break
      case 'day-master':
        params.feature = 'bazi_sharing'
        dispatch('getDayMasterFeedback', params)
        break
      case 'birth-element':
        params.feature = 'base_element_info'
        dispatch('getBirthElementFeedback', params)
        break
      case 'compatibility':
        params.feature = 'compatibility'
        dispatch('getCompatibilityFeedback', params)
        break
      case 'daily-luck':
        params.feature = 'daily_luck'
        dispatch('getDailyLuckFeedback', params)
        break
      case 'ai-conversation-feedback':
        params.feature = 'ai_feedback'
        dispatch('getAiConversationFeedback', params)
        break
      case 'ai-feedback':
        params.feature = 'ai_feedback'
        dispatch('getAiFeedback', params)
        break
      case 'ai-messages-feedback':
        params.feature = 'ai_message_feedback'
        dispatch('getAiMessagesFeedback', params)
        break
      case 'ai-free-flow':
        params.feature = 'ai_message_feedback'
        dispatch('getAiFreeFlowFeedback', params)
        break
      default:
        break
    }
  },
  getGeneralFeedback: async ({ commit, getters }) => {
    commit('setLoadingFeedback', true)
    await axios.get('/feedbacks').then((res) => {
      let feedback = res.data.map((d) => {
        return {
          ...d,
          ...d.response,
          created_at: getDate(d.created_at),
        }
      })
      commit('setFeedback', feedback)
      commit('setLoadingFeedback', false)
    })
  },
  getMonthlyHoroscopeFeedback: async (
    { commit, getters },
    { initSearch, feature }
  ) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
    }
    if (initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    if (!isEmpty(feature)) params.feature = feature
    await axios
      .get('/feature-feedbacks', {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data.map((d) => {
          return {
            ...d,
            ...d.response,
            created_at: getDate(d.created_at),
          }
        })
        commit('setFeedback', feedback)
        // commit('setFeedbackPage', res.data.meta.pagination.pages);
        commit('setTotalFeedbackPage', res.data.meta.pagination.pages)
        commit('setLoadingFeedback', false)
      })
  },
  getProfileFeedback: async ({ commit, getters }, { initSearch, feature }) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
    }
    if (initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    if (!isEmpty(feature)) params.feature = feature
    await axios
      .get('/feature-feedbacks', {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data.map((d) => {
          return {
            ...d,
            ...d.response,
            created_at: getDate(d.created_at),
          }
        })
        commit('setFeedback', feedback)
        // commit('setFeedbackPage', res.data.meta.pagination.pages);
        commit('setTotalFeedbackPage', res.data.meta.pagination.pages)
        commit('setLoadingFeedback', false)
      })
  },
  getDayMasterFeedback: async (
    { commit, getters },
    { initSearch, feature }
  ) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
    }
    if (initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    if (!isEmpty(feature)) params.feature = feature
    await axios
      .get('/feature-feedbacks', {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data.map((d) => {
          return {
            ...d,
            ...d.response,
            created_at: getDate(d.created_at),
          }
        })
        commit('setFeedback', feedback)
        // commit('setFeedbackPage', res.data.meta.pagination.pages);
        commit('setTotalFeedbackPage', res.data.meta.pagination.pages)
        commit('setLoadingFeedback', false)
      })
  },
  getBirthElementFeedback: async (
    { commit, getters },
    { initSearch, feature }
  ) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
    }
    if (initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    if (!isEmpty(feature)) params.feature = feature
    await axios
      .get('/feature-feedbacks', {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data.map((d) => {
          return {
            ...d,
            ...d.response,
            created_at: getDate(d.created_at),
          }
        })
        commit('setFeedback', feedback)
        // commit('setFeedbackPage', res.data.meta.pagination.pages);
        commit('setTotalFeedbackPage', res.data.meta.pagination.pages)
        commit('setLoadingFeedback', false)
      })
  },
  getCompatibilityFeedback: async (
    { commit, getters },
    { initSearch, feature }
  ) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
    }
    if (initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    if (!isEmpty(feature)) params.feature = feature
    await axios
      .get('/feature-feedbacks', {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data.map((d) => {
          return {
            ...d,
            ...d.response,
            created_at: getDate(d.created_at),
          }
        })
        commit('setFeedback', feedback)
        // commit('setFeedbackPage', res.data.meta.pagination.pages);
        commit('setTotalFeedbackPage', res.data.meta.pagination.pages)
        commit('setLoadingFeedback', false)
      })
  },
  getDailyLuckFeedback: async (
    { commit, getters },
    { initSearch, feature }
  ) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
    }
    if (initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    if (!isEmpty(feature)) params.feature = feature
    await axios
      .get('/feature-feedbacks', {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data.map((d) => {
          return {
            ...d,
            ...d.response,
            created_at: getDate(d.created_at),
          }
        })
        commit('setFeedback', feedback)
        // commit('setFeedbackPage', res.data.meta.pagination.pages);
        commit('setTotalFeedbackPage', res.data.meta.pagination.pages)
        commit('setLoadingFeedback', false)
      })
  },

  getAiMessagesFeedback: async (
    { commit, getters },
    { initSearch, feature }
  ) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
    }
    if (initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    if (!isEmpty(feature)) params.feature = feature
    await axios
      .get('/feedbacks/ai-messages', {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data.map((d) => {
          let message = d.answer
          if (isJSON(message)) {
            message = tryParseJSON(message)
            // let messageContent = message.widgets.map((m) =>
            //   m.content ? marked(m.content) : ''
            // )
            let messageContent = message.widgets.map((m) => m.content)
            message = messageContent.join('\n')
          }
          let reasons = d.reason_codes
          if (Array.isArray(reasons)) {
            reasons = d.reason_codes.map((c) => reasonCodes[c])
          } else {
            reasons = ''
          }

          return {
            ...d,
            answer: message,
            reasons: reasons,
            created_at: getDate(d.created_at),
          }
        })
        commit('setFeedback', feedback)
        commit('setTotalFeedbackPage', res.data.meta.pagination.pages)
        commit('setLoadingFeedback', false)
      })
  },
  getAiFreeFlowFeedback: async (
    { commit, getters },
    { initSearch, feature }
  ) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
    }
    if (initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    await axios
      .get('/feedbacks/ai-free-flow', {
        params: params,
      })
      .then((res) => {
        const planMap = [
          { name: 'Trial', value: 'trial', color: '#1877F2' },
          { name: 'Basic', value: 'basic', color: '#888888' },
          { name: 'Premium 1 M', value: '1_month', color: '#F44236' },
          { name: 'Premium 3 M', value: 'three_month', color: '#F44236' },
          { name: 'Premium 6 M', value: 'six_month', color: '#F44236' },
          { name: 'Premium 1 Y', value: 'yearly', color: '#F44236' },
          { name: 'Premium AI 1 M', value: '1_month_ai', color: '#F44236' },
          { name: 'Premium AI 1 Y', value: 'yearly_ai', color: '#F44236' },
          { name: '1 Day Pass', value: 'one_day_pass', color: '#28BF7A' },
          { name: '7 Day Pass', value: 'seven_day_pass', color: '#28BF7A' },
        ]

        const getPlan = (isPremium, plan) => {
          if (!isPremium) {
            return planMap.find((e) => e.value == 'basic').name
          }
          return planMap.find((e) => e.value == plan)?.name || 'Basic'
        }

        const getPlanColor = (isPremium, plan) => {
          if (!isPremium) {
            return planMap.find((e) => e.value == 'basic').color
          }
          return planMap.find((e) => e.value == plan)?.color || '#888888'
        }

        const data = res.data.data.map((e) => {
          return {
            created_at: getDate(e.created_at),
            user_id: e.user_id,
            plan: getPlan(e.is_premium, e.plan),
            color: getPlanColor(e.is_premium, e.plan),
            accuracy_and_relevance: `${e.accuracy_and_relevance}/5`,
            clarity: `${e.clarity}/5`,
            tone_and_personality: `${e.tone_and_personality}/5`,
            message: e.message || '',
          }
        })
        commit('setFeedback', data)
        commit('setTotalFeedbackPage', res.data.meta.pagination.pages)
        commit('setLoadingFeedback', false)
      })
  },
  getAIFeedback: async ({ commit, getters }) => {
    commit('setLoadingFeedback', true)
    await axios.get('/feedbacks/ai').then((res) => {
      let feedback = res.data.map((d) => {
        return {
          ...d,
          ...d.response,
          created_at: getDate(d.created_at),
        }
      })
      commit('setFeedback', feedback)
      commit('setLoadingFeedback', false)
    })
  },
  getAiConversationFeedback: async (
    { commit, getters },
    { initSearch, feature }
  ) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
    }
    if (initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    if (!isEmpty(feature)) params.feature = feature
    await axios
      .get('/ai/chats', {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data.map((d) => {
          return {
            ...d,
            // ...d.response,
            last_messaged_at: formatDateToDDMMMYYYYHHmmString(
              new Date(d.last_messaged_at)
            ),
          }
        })
        commit('setFeedback', feedback)
        // commit('setFeedbackPage', res.data.meta.pagination.pages);
        commit('setTotalFeedbackPage', res.data.meta.pagination.pages)
        commit('setLoadingFeedback', false)
      })
  },
  getCancellationFeedback: async ({ commit, getters }) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
    }
    if (params.initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    await axios
      .get('/feedback/subscription/cancellations', {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data
        commit('setFeedback', feedback)
        // commit('setFeedbackPage', res.data.meta.pagination.pages);
        commit('setTotalFeedbackPage', res.data.meta.pagination.pages)
        commit('setLoadingFeedback', false)
      })
  },
  getAiFeedbackListById: async (
    { commit, getters },
    { initSearch, feature, id }
  ) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
    }
    if (initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    if (!isEmpty(feature)) params.feature = feature
    await axios
      .get(`/ai/chats/${id}/conversations`, {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data.map((d) => {
          return {
            ...d,
            ...d.response,
            user_id: id,
            last_messaged_at: formatDateToDDMMMYYYYHHmmString(
              new Date(d.last_messaged_at)
            ),
          }
        })
        commit('setFeedback', feedback)
        // commit('setFeedbackPage', res.data.meta.pagination.pages);
        commit('setTotalFeedbackPage', res.data.meta.pagination.pages)
        commit('setLoadingFeedback', false)
      })
  },
  getAiFeedbackById: async (
    { commit, getters },
    { initSearch, user_id, conversation_id }
  ) => {
    commit('setLoadingFeedback', true)
    let params = {}
    if (initSearch) {
      params['page'] = 1
      commit('setFeedbackPage', 1)
    }
    await axios
      .get(`/ai/chats/${user_id}/conversations/${conversation_id}/messages`, {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          .map((d) => {
            let message = d.message
            let role = d.role
            if (role == 'assistant') {
              if (isJSON(message)) {
                message = tryParseJSON(message)
                if (message.widgets) {
                  let messageContent = message.widgets.map((m) =>
                    m.content ? marked(m.content) : ''
                  )
                  message = messageContent.join('\n')
                } else {
                  message = d.message
                }
              }
            }
            return {
              ...d,
              message: message,
              created_at: formatDateToDDMMMYYYYHHmmString(
                new Date(d.created_at)
              ),
              has_feedback: !isEmptyObject(
                JSON.parse(JSON.stringify(d.feedback))
              ),
            }
          })
        // .filter((d) => d.role !== 'tool')
        commit('setConversations', feedback)
        commit('setFeedbackPage', 1)
        commit('setTotalFeedbackPage', 1)
        commit('setLoadingFeedback', false)
      })
  },
  appendConversation: async (
    { commit, getters },
    { user_id, conversation_id }
  ) => {
    commit('setLoadingFeedback', true)
    let params = {
      page: getters.currentFeedbackPage,
      limit: 10,
      // beforeId: getters.beforeId
    }
    await axios
      .get(`/ai/chats/${user_id}/conversations/${conversation_id}/messages`, {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          .map((d) => {
            let message = d.message
            let role = d.role
            if (role == 'assistant') {
              if (isJSON(message)) {
                message = tryParseJSON(message)
                if (message.widgets) {
                  let messageContent = message.widgets.map((m) =>
                    m.content ? marked(m.content) : ''
                  )
                  message = messageContent.join('\n')
                } else {
                  message = d.message
                }
              }
            }
            return {
              ...d,
              message: message,
              created_at: formatDateToDDMMMYYYYHHmmString(
                new Date(d.created_at)
              ),
              has_feedback: !isEmptyObject(
                JSON.parse(JSON.stringify(d.feedback))
              ),
            }
          })
        commit('setLoadingFeedback', false)
      })
  },
  getAiFreeFlowFeedbackExport: async ({ commit, getters }) => {
    let params = {
      page: 1,
      limit: 9999,
    }

    params = cleanParams(params)

    try {
      const res = await axios.get('/feedbacks/ai-free-flow', {
        params: params,
      })

      const planMap = [
        { name: 'Trial', value: 'trial', color: '#1877F2' },
        { name: 'Basic', value: 'basic', color: '#888888' },
        { name: 'Premium 1 M', value: '1_month', color: '#F44236' },
        { name: 'Premium 3 M', value: 'three_month', color: '#F44236' },
        { name: 'Premium 6 M', value: 'six_month', color: '#F44236' },
        { name: 'Premium 1 Y', value: 'yearly', color: '#F44236' },
        { name: 'Premium AI 1 M', value: '1_month_ai', color: '#F44236' },
        { name: 'Premium AI 1 Y', value: 'yearly_ai', color: '#F44236' },
        { name: '1 Day Pass', value: 'one_day_pass', color: '#28BF7A' },
        { name: '7 Day Pass', value: 'seven_day_pass', color: '#28BF7A' },
      ]

      const getPlan = (isPremium, plan) => {
        if (!isPremium) {
          return planMap.find((e) => e.value == 'basic').name
        }
        return planMap.find((e) => e.value == plan)?.name || 'Basic'
      }

      if (res.data.data !== null) {
        const data = res.data.data.map((e) => {
          return {
            created_at: new Date(e.created_at).toISOString().slice(0, 10),
            user_id: e.user_id,
            plan: getPlan(e.is_premium, e.plan),
            accuracy_and_relevance: e.accuracy_and_relevance,
            clarity: e.clarity,
            tone_and_personality: e.tone_and_personality,
            message: e.message || '',
          }
        })
        let csvFormat = objectsToCsv(data)
        downloadCsv(csvFormat, `feedback_free_flow_${new Date().getTime()}.csv`)
        return { success: true }
      } else {
        return { false: true, message: 'Export failed: no data' }
      }
    } catch (err) {
      return { success: false, message: err.message }
    }
  },
  getAiFeedbackReportById: async (
    { commit, getters },
    { user_id, conversation_id, title }
  ) => {
    let params = {
      user_id: user_id,
      conversation_id: conversation_id,
    }
    await axios
      .get(`/ai/chats/${user_id}/conversations/${conversation_id}/messages`, {
        params: params,
      })
      .then((res) => {
        let feedback = res.data.data
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          .map((d) => {
            let message = d.message
            let feedback = d.feedback.id_good || ''
            let comment = d.feedback.comment || ''
            let role = d.role
            if (role == 'assistant') {
              if (isJSON(message)) {
                message = tryParseJSON(message)
                if (message.widgets) {
                  let messageContent = message.widgets.map((m) => m.content)
                  message = messageContent.join('\n')
                } else {
                  message = d.message
                }
              }
            }
            return {
              session_id: d.id,
              session_name: title,
              user_id: user_id,
              timestamp: d.created_at,
              sender: d.role,
              message: message,
              input_tokens: d.input_tokens || '',
              output_tokens: d.output_tokens || '',
              feedback: feedback,
              comment: comment,
            }
          })
        const headers = [
          'session_id',
          'session_name',
          'user_id',
          'timestamp',
          'sender',
          'message',
          'input_tokens',
          'output_tokens',
          'feedback',
          'comment',
        ]
        let csv = objectsToCSV(feedback, headers)
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', generateUUIDFilename())
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      })
  },
  getAiFeedbackReport: async (
    { commit, getters, dispatch },
    { user_id = null, start_date = null, end_date = null }
  ) => {
    let params = {
      user_id: user_id,
      start_date: start_date ? moment(start_date).format() : '',
      end_date: end_date ? moment(end_date).format() : '',
    }

    params = cleanParams(params)

    await axios
      .get('/ai/chats/reports', {
        params: params,
        responseType: 'blob',
      })
      .then((res) => {
        const reader = new FileReader()
        reader.onload = () => {
          // Add BOM before the content
          const bom = '\uFEFF'
          const csvWithBom = bom + reader.result

          const blob = new Blob([csvWithBom], {
            type: 'text/csv;charset=utf-8;',
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'all-chats.csv')
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        }

        reader.readAsText(res.data, 'utf-8') // Important: decode blob as text
      })
      .catch((err) => {
        // commit('setIsExportingUser', false)
        // if (err.response.status == 404) {
        //   dispatch('setAlertMessage', {
        //     message: 'Result not found',
        //     type: 'warning',
        //   })
        // } else {
        //   dispatch('setAlertMessage', {
        //     message: 'Server Error',
        //     type: 'red darken-3',
        //   })
        // }
      })
  },
}

// function objectsToCSV(data, headers) {
//   const csvRows = []

//   // 1. Add header row
//   csvRows.push(headers.join(','))

//   // 2. Add data rows
//   for (const row of data) {
//     const values = headers.map((header) => {
//       let val = row[header] ?? ''
//       // Escape double quotes by doubling them, and wrap with quotes if needed
//       val = String(val).replace(/"/g, '""')
//       return `"${val}"`
//     })
//     csvRows.push(values.join(','))
//   }

//   // 3. Add BOM to support Thai characters
//   return '\uFEFF' + csvRows.join('\n')
// }

function generateUUIDFilename(extension = 'csv') {
  const timestamp = Date.now()
  const randomStr = Math.random().toString(36).substring(2, 8)
  return `${randomStr}.${extension}`
}

function cleanParams(params) {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([_, v]) => v !== null && v !== undefined && v !== ''
    )
  )
}

function isEmptyObject(value) {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  )
}

function isJSON(value) {
  if (typeof value !== 'string') return false
  try {
    const parsed = JSON.parse(value)
    return typeof parsed === 'object' && parsed !== null
  } catch (e) {
    return false
  }
}

function tryParseJSON(value) {
  if (typeof value !== 'string') return value
  try {
    const parsed = JSON.parse(value)
    return parsed
  } catch (e) {
    return value
  }
}

function isEmpty(obj) {
  return typeof obj == 'undefined' || obj == '' || obj == null || obj == '-'
}

function getDate(date) {
  const f = new Intl.DateTimeFormat('th', {
    dateStyle: 'long',
  })
  return f.format(new Date(date))
}
export default {
  state,
  mutations,
  actions,
  getters,
}
